<template>
  <div class="sd-header">
    <!-- Main Header Row -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <!-- Left: Logo + Title -->
        <div class="flex items-center gap-3">
          <div class="sd-header__logo">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"></path>
            </svg>
          </div>
          <h1 class="sd-header__title">{{ $t('sales_dashboard.title') }}</h1>
        </div>

        <!-- Right: Navigation -->
        <div class="sd-tabs">
          <router-link 
            :to="{ name: 'sales_dashboard' }"
            class="sd-tabs__trigger"
            :class="{ 'sd-tabs__trigger--active': currentTab === 'overview' }"
          >
            {{ $t('sales_dashboard.overview_tab') }}
          </router-link>
          <router-link 
            :to="{ name: 'sales_budget' }"
            class="sd-tabs__trigger"
            :class="{ 'sd-tabs__trigger--active': currentTab === 'budget' }"
          >
            {{ $t('sales_dashboard.budget_tab') }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Compact Filter Bar -->
    <div v-if="showFilters" class="sd-filter-bar">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <!-- Mobile: Stack filters vertically, Desktop: Single row -->
        <div class="flex flex-col sm:flex-row sm:items-center gap-2">
          
          <!-- Filter Dropdowns Group -->
          <div class="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 flex-1">
            <!-- Salesperson -->
            <select 
              :value="filters.sellerId"
              @change="updateFilter('sellerId', $event.target.value)"
              class="sd-select sd-select--compact w-full sm:w-auto"
            >
              <option value="">{{ $t('sales_dashboard.filter_all_sellers') }}</option>
              <option v-for="seller in sellers" :key="seller.id" :value="seller.id">{{ seller.name }}</option>
            </select>

            <!-- Year -->
            <select 
              :value="filters.year"
              @change="updateFilter('year', $event.target.value)"
              class="sd-select sd-select--compact w-full sm:w-auto"
            >
              <option value="">{{ $t('sales_dashboard.filter_all_years') }}</option>
              <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
            </select>

            <!-- Quarter -->
            <select 
              :value="filters.quarter"
              @change="updateFilter('quarter', $event.target.value)"
              class="sd-select sd-select--compact w-full sm:w-auto"
            >
              <option value="">{{ $t('sales_dashboard.filter_quarter') }}</option>
              <option value="1">Q1</option>
              <option value="2">Q2</option>
              <option value="3">Q3</option>
              <option value="4">Q4</option>
            </select>

            <!-- Month -->
            <select 
              :value="filters.month"
              @change="updateFilter('month', $event.target.value)"
              class="sd-select sd-select--compact w-full sm:w-auto"
            >
              <option value="">{{ $t('sales_dashboard.filter_month') }}</option>
              <option v-for="(month, idx) in months" :key="idx" :value="idx + 1">{{ month }}</option>
            </select>

            <!-- Category -->
            <select 
              :value="filters.category"
              @change="updateFilter('category', $event.target.value)"
              class="sd-select sd-select--compact w-full sm:w-auto col-span-2 sm:col-span-1"
            >
              <option value="">{{ $t('sales_dashboard.filter_all_categories') }}</option>
              <option value="rental">{{ $t('sales_dashboard.category_rental') }}</option>
              <option value="cash_services">{{ $t('sales_dashboard.category_cash_services') }}</option>
              <option value="financed">{{ $t('sales_dashboard.category_financed') }}</option>
            </select>
          </div>

          <!-- Actions Group: Toggle + Apply + Action Buttons -->
          <div class="flex items-center justify-between sm:justify-end gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-200">
            <!-- Date Filter Type Toggle -->
            <div class="flex items-center bg-gray-100 rounded p-0.5">
              <button 
                @click="updateFilter('dateFilterType', 'contract_start')"
                :class="[
                  'px-2 py-0.5 text-xs font-medium rounded transition-all',
                  filters.dateFilterType === 'contract_start' || !filters.dateFilterType
                    ? 'bg-white text-securdia-blue shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                ]"
                :title="$t('sales_dashboard.filter_by_start_date')"
              >
                Start
              </button>
              <button 
                @click="updateFilter('dateFilterType', 'pdf_upload_date')"
                :class="[
                  'px-2 py-0.5 text-xs font-medium rounded transition-all',
                  filters.dateFilterType === 'pdf_upload_date'
                    ? 'bg-white text-green-600 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                ]"
                :title="$t('sales_dashboard.filter_by_sign_date')"
              >
                Sign
              </button>
            </div>

            <!-- Apply Button with clear button -->
            <div class="flex items-center gap-1">
              <button 
                @click="$emit('apply')"
                class="px-3 py-1.5 bg-securdia-blue text-white text-xs font-semibold rounded-md hover:bg-securdia-dark-blue transition-all shadow-sm hover:shadow"
              >
                {{ $t('sales_dashboard.apply_filter') }}
              </button>
              
              <!-- Clear filters X button -->
              <button 
                v-if="activeFilterCount > 0"
                @click="resetFilters"
                class="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                :title="$t('sales_dashboard.clear_filter')"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            <!-- Divider (visible on larger screens) -->
            <div class="hidden md:block w-px h-6 bg-gray-200"></div>

            <!-- Refresh Button -->
            <button 
              @click="$emit('refresh')"
              :disabled="refreshing"
              class="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-semibold text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors disabled:opacity-50"
              :title="$t('sales_dashboard.refresh_data')"
            >
              <svg 
                class="w-3.5 h-3.5" 
                :class="{ 'animate-spin': refreshing }"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              <span class="hidden md:inline">{{ refreshing ? $t('sales_dashboard.refreshing') : $t('sales_dashboard.refresh') }}</span>
            </button>

            <!-- Export Excel -->
            <button 
              @click="$emit('export-excel')"
              class="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
              :title="$t('sales_dashboard.export_excel')"
            >
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/>
              </svg>
              <span class="hidden md:inline">Excel</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- KPI Cards Section -->

  </div>
</template>

<script>
export default {
  name: 'SalesDashboardHeader',
  props: {
    activeTab: {
      type: String,
      required: true,
      validator: (value) => ['overview', 'trends', 'tools', 'budget'].includes(value)
    },
    kpis: {
      type: Object,
      default: () => ({})
    },
    showKpis: {
      type: Boolean,
      default: true
    },
    showFilters: {
      type: Boolean,
      default: true
    },
    filters: {
      type: Object,
      default: () => ({
        sellerId: '',
        year: '',
        quarter: '',
        month: '',
        category: 'rental' // Default to rental agreements
      })
    },
    sellers: {
      type: Array,
      default: () => []
    },
    refreshing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'],
      applyDebounceTimer: null,
      isInitialLoad: true,
      lastAppliedFilters: null // Track last applied filters to prevent loops
    };
  },
  watch: {
    // Auto-apply filters when they change (with debounce)
    filters: {
      handler(newFilters) {
        // Skip the first trigger on mount to avoid double-fetching
        if (this.isInitialLoad) {
          this.isInitialLoad = false;
          this.lastAppliedFilters = JSON.stringify(newFilters);
          return;
        }
        
        // Compare with last applied filters to prevent loops
        const newFiltersStr = JSON.stringify(newFilters);
        if (this.lastAppliedFilters === newFiltersStr) {
          return; // No actual change, skip
        }
        
        this.debouncedApply();
      },
      deep: true
    }
  },
  computed: {
    // Get current tab from route if not explicitly set
    currentTab() {
      if (this.activeTab) return this.activeTab;
      const routeName = this.$route?.name;
      if (routeName === 'sales_tools') return 'tools';
      if (routeName === 'sales_budget') return 'budget';
      return 'overview';
    },
    // Use props kpis or fallback to store
    effectiveKpis() {
      // If kpis prop has data, use it
      if (this.kpis && (this.kpis.mrr || this.kpis.arr || this.kpis.activeContracts)) {
        return this.kpis;
      }
      // Otherwise fallback to Vuex store getter
      return this.$store?.getters['salesDashboard/kpis'] || this.$store?.state?.salesDashboard?.currentData?.kpis || {};
    },
    churnColor() {
      const rate = this.effectiveKpis?.churnRate || 0;
      if (rate > 5) return 'text-red-600';
      if (rate > 3) return 'text-securdia-orange';
      return 'text-green-600';
    },
    availableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= currentYear - 5; y--) {
        years.push(y);
      }
      return years;
    },
    hasActiveFilters() {
      return this.filters.sellerId || this.filters.year || this.filters.quarter || 
             this.filters.month || this.filters.category;
    },
    activeFilterCount() {
      let count = 0;
      if (this.filters.sellerId) count++;
      if (this.filters.year) count++;
      if (this.filters.quarter) count++;
      if (this.filters.month) count++;
      if (this.filters.category) count++;
      return count;
    }
  },
  mounted() {
    // If we don't have KPIs in store, fetch them
    const storeKpis = this.$store?.getters['salesDashboard/kpis'];
    if (!storeKpis || (!storeKpis.totalRevenue && !storeKpis.arr && !storeKpis.totalContracts)) {
      // Dispatch fetch action without showing loading state
      this.$store?.dispatch('salesDashboard/fetchKPIs', { force: false }).catch(() => {
        // Silently ignore errors - data will just show as empty
      });
    }
  },
  beforeDestroy() {
    // Clear debounce timer when component is destroyed
    if (this.applyDebounceTimer) {
      clearTimeout(this.applyDebounceTimer);
    }
  },
  methods: {
    updateFilter(key, value) {
      this.$emit('update:filters', { ...this.filters, [key]: value });
    },
    debouncedApply() {
      // Clear existing timer
      if (this.applyDebounceTimer) {
        clearTimeout(this.applyDebounceTimer);
      }
      // Wait 300ms before applying to avoid too many API calls
      this.applyDebounceTimer = setTimeout(() => {
        // Update lastAppliedFilters to prevent loop
        this.lastAppliedFilters = JSON.stringify(this.filters);
        this.$emit('apply');
      }, 300);
    },
    resetFilters() {
      const newFilters = {
        sellerId: '',
        year: new Date().getFullYear().toString(),
        quarter: '',
        month: '',
        category: '',
        dateFilterType: 'contract_start'
      };
      this.lastAppliedFilters = JSON.stringify(newFilters);
      this.$emit('update:filters', newFilters);
      this.$emit('apply');
    },
    formatCurrency(value) {
      if (!value && value !== 0) return '-';
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },
    formatCompactCurrency(value) {
      if (!value && value !== 0) return '-';
      if (Math.abs(value) >= 1000000) {
        return `${(value / 1000000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })}M kr`;
      }
      if (Math.abs(value) >= 1000) {
        return `${(value / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 0 })}k kr`;
      }
      return this.formatCurrency(value);
    },
    formatPercent(value) {
      if (!value && value !== 0) return '0';
      return value.toFixed(1);
    },
    formatDecimal(value) {
      if (!value && value !== 0) return '-';
      return value.toFixed(1);
    },
    formatYears(months) {
      if (!months) return '0';
      return (months / 12).toFixed(1);
    }
  }
};
</script>

<style scoped>
/* Compact Filter Select Styling */
.filter-select-compact {
  padding: 0.375rem 1.75rem 0.375rem 0.5rem;
  background-color: white;
  border: 1px solid #E5E7EB;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  appearance: none;
  transition: all 0.15s ease;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%231A8DE9' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.25rem center;
  background-repeat: no-repeat;
  background-size: 1.25em 1.25em;
  min-width: 90px;
}

.filter-select-compact:hover {
  border-color: #1A8DE9;
  background-color: rgba(26, 141, 233, 0.03);
}

.filter-select-compact:focus {
  outline: none;
  border-color: #1A8DE9;
  box-shadow: 0 0 0 2px rgba(26, 141, 233, 0.1);
}

/* Option hover styling */
.filter-select-compact option {
  padding: 0.375rem 0.5rem;
  background-color: white;
}

.filter-select-compact option:hover,
.filter-select-compact option:checked {
  background: linear-gradient(to right, #EBF5FF 0%, #EBF5FF 100%);
  color: #1A8DE9;
}
</style>
